import React, { useState, useEffect, useCallback } from 'react';
import { getTheme } from '../theme';
import { S } from '../styles';
import { API, authHeaders } from '../utils/helpers';
import TicketDetail from './TicketDetail';
import CountPill from './CountPill';

// ── Parts to Order (Quotes slice 4, 23 Oct) ──────────────────────────────────
//
// Everyone sees it; everyone can add a note. Managers and Admins add parts and
// remove them, with a reason (parts_order_manage). Removing a part here takes
// it off this list only: it stays on its ticket or quote (25 Oct: "Take off"
// became "Remove", and the page says so). The list is worked out on the server
// from approved quotes and open tickets (helpers/partsOrder.js). Vendor stock
// is looked up in the background; a row says "Checking vendors" until it has
// an answer.

const REASONS = [['ordered', 'Ordered'], ['in_stock', 'In stock after all'], ['not_needed', 'Not needed']];
const day = (iso) => { try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); } catch (_) { return ''; } };
const ago = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 864e5);
  return Number.isFinite(d) ? (d <= 0 ? 'today' : d === 1 ? '1 day' : `${d} days`) : '';
};

async function api(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { credentials: 'include', headers: authHeaders(!!opts.body), ...opts });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Request failed (${r.status})`);
  return d;
}

function VendorStock({ v, T }) {
  if (!v || v.state === 'checking') return <span style={{ color: T.textSub }}>Checking vendors…</span>;
  if (v.state === 'no_code') return <span style={{ color: T.textSub }} title="No UPC or vendor part number to look the part up by">No part number to look up</span>;
  if (v.state === 'no_listing') return <span style={{ color: T.textSub }}>Not in your vendor catalogs</span>;
  if (v.state === 'out_everywhere') return <span style={{ color: '#e74c3c' }}>Not in stock at vendors</span>;
  const inStock = (v.vendors || []).filter(x => x.stock > 0);
  return (
    <span>
      {inStock.map((x, i) => (
        <span key={i} style={{ display: 'block', color: '#2ecc71' }}>{x.vendor}: {x.stock} in stock</span>
      ))}
      {v.stale && <span style={{ fontSize: 11, color: T.textSub }}>updating…</span>}
    </span>
  );
}

function Row({ r, T, canManage, onRemove, onNote, onOpenTicket, busyKey }) {
  const [mode, setMode] = useState(null); // 'remove' | 'note'
  const [reason, setReason] = useState('ordered');
  const [text, setText] = useState('');
  const td = { padding: '11px 12px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.border}`, verticalAlign: 'top' };
  const busy = busyKey === r.key;
  const label = `${r.part.description}${r.ticket ? ` (ticket #${r.ticket.id})` : r.quote ? ` (${r.quote.label})` : ''}`;
  const input = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', fontSize: 12, borderRadius: 6, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit' };
  return (
    <>
      <tr>
        <td style={td}>
          <div style={{ fontWeight: 600 }}>{r.part.description}</div>
          {/* Every code the part has (25 Oct), so it can be found at any
              vendor or in Lightspeed: vendor and its part number first. */}
          <div style={{ fontSize: 11, color: T.textSub, marginTop: 2, lineHeight: 1.6 }}>
            {[
              r.part.vendor && (r.part.vendorPart ? `${r.part.vendor} # ${r.part.vendorPart}` : r.part.vendor),
              !r.part.vendor && r.part.vendorPart && `Vendor # ${r.part.vendorPart}`,
              r.part.manufacturerSku && `Mfr SKU ${r.part.manufacturerSku}`,
              r.part.upc && `UPC ${r.part.upc}`,
              r.part.ean && `EAN ${r.part.ean}`,
              r.part.customSku && `SKU ${r.part.customSku}`,
              r.part.systemSku && `System ID ${r.part.systemSku}`,
            ].filter(Boolean).map((t, i) => (
              <span key={i} style={{ whiteSpace: 'nowrap', userSelect: 'all' }}>{i > 0 && <span style={{ margin: '0 5px' }}>·</span>}{t}</span>
            ))}
          </div>
        </td>
        <td style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{r.quantity}</td>
        <td style={td}>
          {r.quote && (
            <button onClick={() => window.dispatchEvent(new CustomEvent('spinboard-nav', { detail: { tab: 'quotes', quoteId: r.quote.id } }))}
              style={{ background: 'none', border: 'none', padding: 0, color: '#3498db', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{r.quote.label}</button>
          )}
          {r.quote && r.ticket && <span style={{ color: T.textSub }}> · </span>}
          {r.ticket && (
            <button onClick={() => onOpenTicket(r.ticket.id)} style={{ background: 'none', border: 'none', padding: 0, color: '#3498db', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>#{r.ticket.id}</button>
          )}
          <div style={{ fontSize: 12, marginTop: 2 }}>{(r.quote && r.quote.customer) || (r.ticket && r.ticket.customer) || r.forText || ''}</div>
          <div style={{ fontSize: 11, color: T.textSub }}>
            {r.ticket ? [r.ticket.bike, r.ticket.status].filter(Boolean).join(' · ') : r.notOnTicketYet ? 'Approved, not on its ticket yet' : r.addedBy ? `Added by ${r.addedBy}` : ''}
          </div>
        </td>
        <td style={{ ...td, whiteSpace: 'nowrap', color: T.textSub }}>
          {r.quote && r.quote.approvedAt ? `Approved ${day(r.quote.approvedAt)}` : r.ticket && r.ticket.opened ? `${ago(r.ticket.opened)}` : r.addedAt ? day(r.addedAt) : ''}
        </td>
        <td style={{ ...td, fontSize: 12 }}><VendorStock v={r.vendorStock} T={T} /></td>
        <td style={{ ...td, fontSize: 12 }}>
          {r.notes.map(n => (
            <div key={n.id} style={{ marginBottom: 4 }}><span style={{ color: T.textSub }}>{n.by || 'staff'}, {day(n.at)}:</span> {n.body}</div>
          ))}
          <button onClick={() => setMode(mode === 'note' ? null : 'note')} style={{ background: 'none', border: 'none', padding: 0, color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
            {r.notes.length ? 'Add a note' : 'Note'}
          </button>
        </td>
        <td style={{ ...td, textAlign: 'right' }}>
          {canManage && (
            <button onClick={() => setMode(mode === 'remove' ? null : 'remove')} style={{ ...S.btn(false), padding: '5px 10px', fontSize: 12, color: T.text, borderColor: T.border }}>Remove</button>
          )}
        </td>
      </tr>
      {mode && (
        <tr>
          <td colSpan={7} style={{ padding: '0 12px 12px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', maxWidth: 760, marginLeft: 'auto' }}>
              {mode === 'remove' && (
                <select value={reason} onChange={e => setReason(e.target.value)} style={{ ...input, width: 190 }}>
                  {REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              )}
              <input value={text} onChange={e => setText(e.target.value)} autoFocus style={input}
                placeholder={mode === 'remove' ? 'Note, e.g. "QBP order 4471, arrives Tue" (optional)' : 'Note for whoever orders'} />
              <button disabled={busy || (mode === 'note' && !text.trim())}
                onClick={async () => { const ok = mode === 'remove' ? await onRemove(r.key, reason, text, label) : await onNote(r.key, text); if (ok) { setMode(null); setText(''); } }}
                style={{ ...S.btn(true), whiteSpace: 'nowrap', background: T.btnPrimary, color: T.btnPrimaryText, opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Saving…' : mode === 'remove' ? 'Remove from this list' : 'Save note'}
              </button>
              <button onClick={() => { setMode(null); setText(''); }} style={{ ...S.btn(false), color: T.textSub, borderColor: T.border }}>Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function PartsToOrder({ theme, currentUser = null }) {
  const T = getTheme(theme);
  const canManage = !!(currentUser && currentUser.permissions && currentUser.permissions.parts_order_manage);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [busyKey, setBusyKey] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [add, setAdd] = useState({ description: '', vendor: '', vendorPart: '', quantity: 1, forText: '' });
  const [showRemoved, setShowRemoved] = useState(false);
  const [emailTo, setEmailTo] = useState(null);   // who "Email this list now" reaches

  const load = useCallback((fresh = false) => api(`/api/parts-order${fresh ? '?fresh=1' : ''}`).then(d => { setData(d); setEmailTo(d.emailTo || null); setError(''); }).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  // While vendor lookups are running, look again every 10 seconds (cheap: the
  // Lightspeed part is cached on the server); otherwise every two minutes.
  const checking = data && [...data.quotes, ...data.tickets, ...data.added].some(r => r.vendorStock && r.vendorStock.state === 'checking');
  useEffect(() => {
    const t = setInterval(() => load(), checking ? 10000 : 120000);
    return () => clearInterval(t);
  }, [checking, load]);

  const act = async (key, path, body) => {
    setBusyKey(key);
    try { setData(await api(path, { method: 'POST', body: JSON.stringify(body) })); setBusyKey(null); return true; }
    catch (e) { setError(e.message); setBusyKey(null); return false; }
  };
  const onRemove = (key, reason, note, label) => act(key, '/api/parts-order/remove', { key, reason, note, label });
  const onNote = (key, body) => act(key, '/api/parts-order/note', { key, body });
  // The daily email, now (24 Oct): same email, same people as Settings.
  const [emailing, setEmailing] = useState(false);
  const [emailed, setEmailed] = useState('');
  const emailNow = async () => {
    if (!window.confirm('Email this list now to the people picked in Settings?')) return;
    setEmailing(true); setError(''); setEmailed('');
    try { const r = await api('/api/parts-order/email-now', { method: 'POST' }); setEmailed(`Sent to ${r.to} ${r.to === 1 ? 'person' : 'people'}.`); }
    catch (e) { setError(e.message); }
    setEmailing(false);
  };

  // Filters and sorting (25 Oct). Kept for this screen only; they narrow every
  // section the same way, and the counts show what each section holds in all.
  const [fVendor, setFVendor] = useState('');
  const [fStock, setFStock] = useState('');
  const [sortBy, setSortBy] = useState('waiting');
  const allRows = data ? [...data.quotes, ...data.tickets, ...data.added] : [];
  const vendorsOnList = [...new Set(allRows.map(r => r.part.vendor || ''))].sort((a, b) => (a === '') - (b === '') || a.localeCompare(b));
  const stockOf = (r) => {
    const st = r.vendorStock && r.vendorStock.state;
    if (st === 'in_stock') return 'in';
    if (st === 'out_everywhere') return 'out';
    return 'unknown';
  };
  const since = (r) => (r.quote && r.quote.approvedAt) || (r.ticket && r.ticket.opened) || r.addedAt || '';
  const sorters = {
    waiting: (a, b) => String(since(a)).localeCompare(String(since(b))),
    newest: (a, b) => String(since(b)).localeCompare(String(since(a))),
    vendor: (a, b) => (a.part.vendor || '~').localeCompare(b.part.vendor || '~') || a.part.description.localeCompare(b.part.description),
    part: (a, b) => a.part.description.localeCompare(b.part.description),
  };
  const filtersOn = fVendor !== '' || fStock !== '';
  const passes = (r) => (fVendor === '' || (fVendor === '(none)' ? !r.part.vendor : r.part.vendor === fVendor)) && (fStock === '' || stockOf(r) === fStock);

  const q = search.trim().toLowerCase();
  const match = (r) => passes(r) && (!q || [r.part.description, r.part.vendor, r.part.vendorPart, r.part.upc, r.quote && r.quote.label, r.quote && r.quote.customer,
    r.ticket && r.ticket.id, r.ticket && r.ticket.customer, r.forText, r.part.manufacturerSku, r.part.ean, r.part.customSku, r.part.systemSku].filter(Boolean).join(' ').toLowerCase().includes(q));

  const th = { textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textSub, textTransform: 'uppercase', letterSpacing: 0.5, padding: '10px 12px', borderBottom: `1px solid ${T.border}` };
  const section = (title, rows, blurb, empty) => {
    const shown = rows.filter(match).sort(sorters[sortBy] || sorters.waiting);
    return (
      <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 16px 6px', display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{title}</span>
          <CountPill n={rows.length} T={T} tone="orange" />
          <span style={{ fontSize: 12, color: T.textSub, marginLeft: 6 }}>{blurb}</span>
        </div>
        {shown.length === 0 ? (
          <div style={{ padding: '10px 16px 18px', fontSize: 13, color: T.textSub }}>{(q || filtersOn) && rows.length ? 'Nothing here matches the search or filters.' : empty}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Part</th><th style={{ ...th, textAlign: 'center' }}>Qty</th><th style={th}>For</th><th style={th}>Waiting</th>
              <th style={th}>Vendor stock</th><th style={th}>Notes</th><th style={th} />
            </tr></thead>
            <tbody>
              {shown.map(r => <Row key={r.key} r={r} T={T} canManage={canManage} onRemove={onRemove} onNote={onNote} onOpenTicket={setTicketId} busyKey={busyKey} />)}
            </tbody>
          </table>
        )}
      </div>
    );
  };
  const input = { padding: '8px 10px', fontSize: 13, borderRadius: 7, background: T.input, border: `1px solid ${T.inputBorder}`, color: T.text, fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: T.text, flex: 1 }}>
          Parts to Order {data && <CountPill n={data.count} T={T} tone="orange" size={15} style={{ marginLeft: 6, verticalAlign: 'middle' }} />}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search part, vendor, customer, ticket or Q-number" style={{ ...input, width: 320 }} />
        <button onClick={() => load(true)} style={{ ...S.btn(false), color: T.text, borderColor: T.border }}>Refresh</button>
        {canManage && <button onClick={emailNow} disabled={emailing || !data || !data.count} title={emailTo && emailTo.length ? `Email to ${emailTo.join(', ')}` : 'Nobody is picked to get it yet: Settings, Service Workflow, Parts to Order email'}
          style={{ ...S.btn(false), color: T.text, borderColor: T.border, opacity: data && data.count ? 1 : 0.5 }}>{emailing ? 'Sending…' : 'Email this list now'}</button>}
        {canManage && <button onClick={() => setShowAdd(v => !v)} style={{ ...S.btn(true), background: T.btnPrimary, color: T.btnPrimaryText }}>+ Add part</button>}
      </div>
      {emailed && <div style={{ fontSize: 12, color: '#27ae60', marginBottom: 6 }}>{emailed}</div>}
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 16 }}>
        Parts that are not in stock, from approved quotes and open tickets. Removing a part here takes it off this list only; it stays on its ticket or quote.
        {data && data.asOf ? ` Checked ${new Date(data.asOf).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}.` : ''}
        {data && data.degraded ? ' Lightspeed is slow to answer right now, so this may be a few minutes behind.' : ''}
      </div>
      {/* Estimates (26 Oct): parts on a ticket out for an estimate wait here
          until the customer approves it. */}
      {data && data.heldForEstimate && data.heldForEstimate.length > 0 && (
        <div style={{ fontSize: 12, color: T.textSub, margin: '-8px 0 16px', lineHeight: 1.6 }}>
          Waiting on an estimate answer, not listed yet: {data.heldForEstimate.map(h => `ticket #${h.ticketId} (${h.estimate}${h.customer ? `, ${h.customer}` : ''}, ${h.parts} part${h.parts !== 1 ? 's' : ''})`).join('; ')}.
          They join the list when the customer approves.
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <select value={fVendor} onChange={e => setFVendor(e.target.value)} style={{ ...input, width: 'auto' }} aria-label="Vendor">
            <option value="">All vendors</option>
            {vendorsOnList.map(v => <option key={v || '(none)'} value={v || '(none)'}>{v || 'No vendor set'}</option>)}
          </select>
          <select value={fStock} onChange={e => setFStock(e.target.value)} style={{ ...input, width: 'auto' }} aria-label="Vendor stock">
            <option value="">Any vendor stock</option>
            <option value="in">In stock at a vendor</option>
            <option value="out">Not in stock at vendors</option>
            <option value="unknown">Not known (no part number or not in catalogs)</option>
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...input, width: 'auto' }} aria-label="Sort">
            <option value="waiting">Waiting longest first</option>
            <option value="newest">Newest first</option>
            <option value="vendor">By vendor</option>
            <option value="part">By part name</option>
          </select>
          {filtersOn && (
            <button onClick={() => { setFVendor(''); setFStock(''); }} style={{ background: 'none', border: 'none', color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>Clear filters</button>
          )}
        </div>
      )}

      {showAdd && canManage && (
        <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 70px 1.5fr auto', gap: 8, alignItems: 'center' }}>
          <input style={input} placeholder="Part description" value={add.description} onChange={e => setAdd({ ...add, description: e.target.value })} autoFocus />
          <input style={input} placeholder="Vendor" value={add.vendor} onChange={e => setAdd({ ...add, vendor: e.target.value })} />
          <input style={input} placeholder="Vendor part number" value={add.vendorPart} onChange={e => setAdd({ ...add, vendorPart: e.target.value })} />
          <input style={input} type="number" min="1" value={add.quantity} onChange={e => setAdd({ ...add, quantity: e.target.value })} />
          <input style={input} placeholder="For (e.g. shop stock)" value={add.forText} onChange={e => setAdd({ ...add, forText: e.target.value })} />
          <button disabled={!add.description.trim() || busyKey === 'add'}
            onClick={async () => { if (await act('add', '/api/parts-order/add', add)) { setAdd({ description: '', vendor: '', vendorPart: '', quantity: 1, forText: '' }); setShowAdd(false); } }}
            style={{ ...S.btn(true), background: T.btnPrimary, color: T.btnPrimaryText, opacity: add.description.trim() ? 1 : 0.5 }}>Add</button>
        </div>
      )}

      {error && <div style={{ color: '#e74c3c', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {!data ? <div style={{ color: T.textSub, fontSize: 13 }}>Working out what needs ordering… (it reads every open ticket the first time)</div> : (
        <>
          {section('Approved quotes', data.quotes, 'parts customers have said yes to', 'Nothing to order from approved quotes.')}
          {section('Open tickets', data.tickets, 'parts on tickets with not enough in stock', 'Every part on an open ticket is in stock.')}
          {section('Added by hand', data.added, 'put on the list by a manager', canManage ? 'Use + Add part for anything else that needs ordering.' : 'Nothing added by hand.')}

          {data.recentlyRemoved.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <button onClick={() => setShowRemoved(v => !v)} style={{ background: 'none', border: 'none', padding: 0, color: T.textSub, textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
                {showRemoved ? 'Hide' : 'Show'} recently removed ({data.recentlyRemoved.length})
              </button>
              {showRemoved && (
                <div style={{ marginTop: 8, fontSize: 12, color: T.textSub, lineHeight: 1.8 }}>
                  {data.recentlyRemoved.map(r => (
                    <div key={r.key}><span style={{ color: T.text }}>{r.label || r.key}</span>: {r.reason}{r.note ? ` (${r.note})` : ''}, by {r.by || 'staff'}, {day(r.at)}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {ticketId && <TicketDetail ticketId={ticketId} onClose={() => { setTicketId(null); }} theme={theme} currentUser={currentUser} />}
    </div>
  );
}
